/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { useCallback, useState } from 'react';

import { type HtmlAttributes } from './types';

type UseSemiControlledInputProps<T> = {
  state: T;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isControlled?: boolean;
  inputType: HtmlAttributes<'input'>['type'];
};

export function useSemiControlledInput<T>({
  state,
  onChange,
  isControlled,
  inputType = 'text',
}: UseSemiControlledInputProps<T>): [
  T,
  (event: React.ChangeEvent<HTMLInputElement>) => void,
] {
  const [internalState, setInternalState] = useState<T>(state);

  const current = isControlled === true ? state : internalState;

  const setStateFromEvent = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let resolved: T;

      if (['checkbox', 'radio', 'switch'].includes(inputType)) {
        resolved = event.target.checked as T;
      } else {
        resolved = event.target.value as T;
      }

      if (isControlled !== true) {
        setInternalState(resolved);
      }

      onChange?.(event);
    },
    [isControlled, onChange, inputType],
  );

  return [current, setStateFromEvent];
}
